// Money-side report tabs: R1 costs, R4 hours, R5 parts. Costs/parts are only reachable with
// canViewCosts (tab hidden AND server-enforced). Negative actuals are real CO behavior
// (settlement credits) and are shown, not hidden.
import React from 'react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card } from '../ui';
import { useReport, Kpi, BarList, csvDownload, ExportButton } from './ReportsScreen';
import { getReportCosts, getReportHours, getReportParts } from '../../api';

const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Money must always carry its unit. PMCO holds one row set per currency (COCUR is a KEY field), so
// before the ABAP pinned the currency these figures were two currencies added together and printed
// bare — see PM-SAP-FIELD-TRANSPARENCY.md §6.1. `currency` is now reported by the handler; when a
// selection still spans company codes it also sets `currencyMixed`, and we say so rather than
// labelling a mixed total with one arbitrary code.
const money = (v, currency) => (currency ? `${fmt(v)} ${currency}` : fmt(v));

function Ladder({ error, data, retry, children }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';
  if (error) return <Card><p className={sub}>{error} <button type="button" className="underline" onClick={retry}>{t('pm.common.retry')}</button></p></Card>;
  if (data === null) return <Card><p className={sub}>{t('pm.common.loading')}</p></Card>;
  return children(data);
}

export function CostsTab({ me, user }) {
  const { t } = useLanguage();
  const role = me.profile.id;
  const rep = useReport(() => getReportCosts(user, { plant: me.activePlant }, role), [user, me.activePlant, role]);

  return (
    <Ladder {...rep}>
      {(d) => (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Kpi label={t('pm.rep.planTotal')} value={money(d.planTotal, d.currency)} />
            <Kpi label={t('pm.rep.actualTotal')} value={money(d.actualTotal, d.currency)} />
            <Kpi label={t('pm.rep.corrective')} value={money(d.correctiveActual, d.currency)} tone="warn" />
            <Kpi label={t('pm.rep.preventive')} value={money(d.preventiveActual, d.currency)} tone="good" />
          </div>
          {d.currencyMixed && (
            <Card><p className="text-sm text-amber-600 dark:text-amber-300">{t('pm.rep.currencyMixed')}</p></Card>
          )}
          <Card>
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-sm">{t('pm.rep.byType')} ({d.year})</p>
              <ExportButton onClick={() => csvDownload(`pm-costs-${d.year}.csv`,
                [{ key: 'orderType', label: t('pm.rep.orderType') }, { key: 'plan', label: t('pm.rep.planTotal') }, { key: 'actual', label: t('pm.rep.actualTotal') }],
                d.byType)} />
            </div>
            <BarList rows={d.byType.map((x) => ({ label: x.orderType, value: x.actual, display: fmt(x.actual) }))} />
          </Card>
          <Card>
            <p className="font-semibold text-sm mb-2">{t('pm.rep.monthlyActual')}</p>
            <BarList rows={d.monthlyActual.map((v, i) => ({ label: String(i + 1).padStart(2, '0'), value: v, display: fmt(v) }))} />
          </Card>
          <Card>
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-sm">{t('pm.rep.topOrders')}</p>
              <ExportButton onClick={() => csvDownload('pm-top-orders.csv',
                [{ key: 'aufnr', label: t('pm.rep.order') }, { key: 'description', label: t('pm.rep.description') }, { key: 'actual', label: t('pm.rep.actualTotal') }],
                d.topOrders)} />
            </div>
            <BarList rows={d.topOrders.map((o) => ({ label: `${o.aufnr} ${o.description}`, value: o.actual, display: fmt(o.actual) }))} />
          </Card>
          <Card>
            <p className="font-semibold text-sm mb-2">{t('pm.rep.topEquipment')}</p>
            <BarList rows={d.topEquipment.map((e) => ({ label: `${e.equipment} ${e.description}`, value: e.actual, display: fmt(e.actual) }))} />
          </Card>
        </div>
      )}
    </Ladder>
  );
}

export function HoursTab({ me, user }) {
  const { t } = useLanguage();
  const role = me.profile.id;
  const rep = useReport(() => getReportHours(user, { plant: me.activePlant }, role), [user, me.activePlant, role]);

  return (
    <Ladder {...rep}>
      {(d) => (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Kpi label={t('pm.rep.totalHours')} value={`${fmt(d.totalHours)} h`} />
          </div>
          <Card>
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-sm">{t('pm.rep.byWorkCenter')} ({d.year})</p>
              <ExportButton onClick={() => csvDownload(`pm-hours-${d.year}.csv`,
                [{ key: 'workCenter', label: t('pm.rep.workCenter') }, { key: 'hours', label: t('pm.rep.hours') }, { key: 'confirmations', label: t('pm.rep.confirmations') }],
                d.byWorkCenter)} />
            </div>
            <BarList rows={d.byWorkCenter.map((w) => ({ label: w.workCenter || '—', value: w.hours, display: `${fmt(w.hours)} h · ${w.confirmations}×` }))} />
          </Card>
          <Card>
            <p className="font-semibold text-sm mb-2">{t('pm.rep.byMonth')}</p>
            <BarList rows={d.byMonth.map((m) => ({ label: m.month, value: m.hours, display: `${fmt(m.hours)} h` }))} />
          </Card>
        </div>
      )}
    </Ladder>
  );
}

export function PartsTab({ me, user }) {
  const { t } = useLanguage();
  const role = me.profile.id;
  const rep = useReport(() => getReportParts(user, { plant: me.activePlant }, role), [user, me.activePlant, role]);

  return (
    <Ladder {...rep}>
      {(d) => (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Kpi label={t('pm.rep.totalValue')} value={fmt(d.totalValue)} />
            <Kpi label={t('pm.rep.windowDays')} value={d.windowDays} />
          </div>
          <Card>
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-sm">{t('pm.rep.topMaterials')}</p>
              <ExportButton onClick={() => csvDownload('pm-parts.csv',
                [{ key: 'materialId', label: t('pm.rep.material') }, { key: 'description', label: t('pm.rep.description') }, { key: 'qty', label: t('pm.rep.qty') }, { key: 'unit', label: t('pm.rep.unit') }, { key: 'value', label: t('pm.rep.value') }],
                d.topMaterials)} />
            </div>
            <BarList rows={d.topMaterials.map((m) => ({ label: `${m.materialId} ${m.description}`, value: m.value, display: `${fmt(m.qty)} ${m.unit} · ${fmt(m.value)}` }))} />
          </Card>
          <Card>
            <p className="font-semibold text-sm mb-2">{t('pm.rep.topEquipment')}</p>
            <BarList rows={d.topEquipment.map((e) => ({ label: `${e.equipment} ${e.description}`, value: e.value, display: fmt(e.value) }))} />
          </Card>
        </div>
      )}
    </Ladder>
  );
}
